import { styled } from "@linaria/react";
import * as React from "react";
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router";
import t from "../../../../i18n";
import tUI from "@ui/i18n";
import { color } from "@ui";
import Ellipsis from "@ui/Ellipsis";
import SecondaryHeader from "@ui/Header/SecondaryHeader";
import Link from "@ui/Link";
import type { Project } from "../../../../api/useProjects";
import SecondaryDropdownMenu from "./HeaderDropdownMenu";
import { SetupNavBar } from "./NavBar";
import formatDate from "@ui/i18n/formatDate";
import type { Setup } from "../../../../api/useSetups";
import { computeEndDate, useSetups } from "../../../../api/useSetups";
import BlankStateV2 from "@ui/BlankStateV2";
import { useSetupActiveAnalysisCount } from "../../../../api/useActiveAnalysesStream";
import { toPath } from "../../../../router/helpers/path";

const BreadcrumbWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const StyledLink = styled(Link)`
  color: ${color.lightNitrogen500_Main};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
`;

const Name = styled.div`
  margin-right: 8px;
`;

const Description = styled.div`
  display: flex;
  font-weight: 500;
  max-width: 200px;
`;

const SetupInfos = styled.div`
  display: flex;
  align-items: center;
`;

const ActiveAnalysisCount = styled.span`
  display: flex;
  align-items: center;
`;

interface B {
  project: Project;
  setup: Setup;
  setups: Setup[];
}

function Breadcrumb({ project, setup, setups }: B) {
  return (
    <BreadcrumbWrapper>
      <StyledLink to={toPath("/projects/:projectId/settings", { projectId: project.id })}>
        {t("project.secondary_header.settings.title")}
      </StyledLink>
      <div>{">"}</div>
      <SetupInfos>
        <Name title={setup.name}>{setup.name}</Name>
        {setup.description && (
          <Description>
            (<Ellipsis title={setup.description}>{setup.description}</Ellipsis>)
          </Description>
        )}
      </SetupInfos>
      <SecondaryDropdownMenu project={project} setup={setup} setups={setups} />
    </BreadcrumbWrapper>
  );
}

export const ProjectSetup = React.memo(() => {
  const project = useOutletContext<Project>();
  const { setupId } = useParams();

  if (!project) throw new Error("no project");
  if (!setupId) throw new Error("no setupId");

  const navigate = useNavigate();
  const setups = useSetups(project.id);
  const { pathname } = useLocation();
  const activeAnalysisCount = useSetupActiveAnalysisCount(project.id, setupId);

  const setup = React.useMemo(
    () => setups.find((setup: { id: string }) => setup.id === setupId),
    [setupId, setups],
  );

  if (!setup)
    return (
      <BlankStateV2
        url={pathname}
        subtitle={tUI("404.description")}
        onClick={() => navigate("")}
        buttonLabel={tUI("404.action")}
      />
    );

  const startDate =
    setup.startDate === null ? null : formatDate(setup.startDate, "date", project.timezone);
  let endDate = computeEndDate(setup, setups);
  endDate = endDate === null ? null : formatDate(endDate, "date", project.timezone);

  return (
    <>
      <SecondaryHeader
        breadcrumb={<Breadcrumb project={project} setup={setup} setups={setups} />}
        rightSection={
          <ActiveAnalysisCount>
            {t("project.secondary_header.setup.setup_and_analysis_infos", {
              startDate,
              endDate,
              count: activeAnalysisCount,
            })}
          </ActiveAnalysisCount>
        }
        nav={<SetupNavBar projectId={project.id} setup={setup} />}
      />
      <Outlet context={{ project, setup }} />
    </>
  );
});
